import {
    Close as CloseIcon,
    Print as PrintIcon,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import React from "react";

// Print Report Component
const PrintCashCloseReport = ({ 
    reportData, 
    onClose, 
    onPrint 
}) => {
    const {
        posProfile,
        submittedBy,
        openingEntry,
        creationDate,
        transactionSummary,
        exchangeRates,
        actualAmounts,
        cashOutTransactions,
        cashOutTotal,
        calculations
    } = reportData;

    const printStyles = `
        <style>
            @media print {
                body { margin: 0; }
            }
            .print-format {
                font-size: 10pt;
                font-family: 'Courier New', monospace;
                width: 300px;
                margin: 0 auto;
                padding: 10px;
                line-height: 1.2;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .table { 
                width: 100%; 
                border-collapse: collapse; 
                margin: 4px 0;
            }
            .table th, .table td { 
                text-align: left; 
                padding: 1px 2px;
                font-size: 9pt;
            }
            .table th {
                border-bottom: 1px solid #000;
            }
            .section-title {
                margin-top: 8px;
                font-weight: bold;
                border-top: 1px dashed #000;
                padding-top: 4px;
            }
            hr {
                border: none;
                border-top: 1px solid #000;
                margin: 8px 0;
            }
            .dashed-line {
                border-top: 1px dashed #000;
                margin: 4px 0;
            }
            .right { text-align: right; }
        </style>
    `;

    const reportHTML = `
        ${printStyles}
        <div class="print-format">
            <div class="center bold">POS CASH CLOSING REPORT</div>
            <div class="center">${new Date().toISOString().split('T')[0]}-${Math.random().toString(36).slice(2, 11)}</div>
            <hr />

            <b>POS Profile:</b> ${posProfile}<br />
            <b>Submitted By:</b> ${submittedBy}<br />
            <b>Opening Entry:</b> ${openingEntry}<br />
            <b>Date:</b> ${new Date(creationDate).toLocaleDateString()} ${new Date(creationDate).toLocaleTimeString()}<br />

            <div class="section-title">TRANSACTION SUMMARY</div>
            Invoices: ${transactionSummary.invoices}<br />
            Total Sales: ₪${transactionSummary.totalSales.toFixed(2)}<br />
            Total Items: ${transactionSummary.totalItems}<br />
            Opening Cash: ₪${transactionSummary.openingCash.toFixed(2)}<br />
            Cash-Out Total: ₪${cashOutTotal.toFixed(2)}

            <div class="section-title">EXCHANGE RATES</div>
            $1 = ₪${exchangeRates.USD}<br />
            JD1 = ₪${exchangeRates.JOD}<br />

            <div class="section-title">ACTUAL CASH COUNT</div>
            Main Currency (ILS): ₪${actualAmounts.ILS}<br />
            USD: $${actualAmounts.USD}<br />
            JOD: JD${actualAmounts.JOD}<br />
            Visa: ₪${actualAmounts.VISA}

            <div class="section-title">CASH-OUT TRANSACTIONS</div>
            ${cashOutTransactions.length > 0 ? `
                <table class="table">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Description</th>
                            <th class="right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${cashOutTransactions.map(tx => `
                            <tr>
                                <td>${tx.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                                <td>${tx.description || 'N/A'}</td>
                                <td class="right">₪${tx.amount.toFixed(2)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            ` : '<p>No cash-out transactions.</p>'}
            <b>Total Cash-Out:</b> ₪${cashOutTotal.toFixed(2)}

            <div class="section-title">CALCULATION SUMMARY</div>
            Expected Cash = Opening + Sales - Cash-Out<br />
            Expected = ₪${calculations.openingCash.toFixed(2)} + ₪${calculations.salesTotal.toFixed(2)} - ₪${cashOutTotal.toFixed(2)} = ₪${calculations.expectedCash.toFixed(2)}<br />
            Actual Cash: ₪${calculations.actualCash.toFixed(2)}<br />
            <b>Difference:</b> ₪${calculations.difference.toFixed(2)}${calculations.difference > 0 ? ' (Over)' : calculations.difference < 0 ? ' (Short)' : ' (Balanced)'}

            <div class="dashed-line"></div>
            <div class="center">-- END OF REPORT --</div>
            <div class="center" style="margin-top: 10px; font-size: 8pt;">
                Printed on: ${new Date().toLocaleString()}
            </div>
        </div>
    `;

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>POS Cash Closing Report</title>
                    <meta charset="utf-8">
                </head>
                <body>
                    ${reportHTML}
                    <script>
                        window.onload = function() {
                            window.print();
                            window.onafterprint = function() {
                                window.close();
                            };
                        };
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
        if (onPrint) onPrint();
    };

    return (
        <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Print Cash Closing Report</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {/* Print Preview */}
                <Box 
                    sx={{ 
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                        maxHeight: '400px',
                        overflow: 'auto',
                        bgcolor: '#f9f9f9'
                    }}
                >
                    <div 
                        dangerouslySetInnerHTML={{ __html: reportHTML }}
                        style={{ transform: 'scale(0.8)', transformOrigin: 'top left' }}
                    />
                </Box>
                
                <Box display="flex" justifyContent="space-between" gap={2}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handlePrint}
                        startIcon={<PrintIcon />}
                    >
                        Print Report
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PrintCashCloseReport; 